import { Link } from 'react-router-dom';
import { useCoafDashboard, useCoafDeadlines, getStatusLabel, getStatusColor, getTypeLabel } from '../../../hooks/useCoaf';
import { FileText, Clock, AlertTriangle, CheckCircle, Send, XCircle, Plus, ArrowRight } from 'lucide-react';

export default function CoafDashboard() {
  const { data: dashboard, isLoading, error } = useCoafDashboard();
  const { data: deadlines } = useCoafDeadlines();

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-700 rounded w-1/4"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-surface-700 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
          Erro ao carregar dashboard COAF
        </div>
      </div>
    );
  }

  const stats = [
    {
      label: 'Total de Comunicações',
      value: dashboard?.totalCommunications || 0,
      icon: FileText,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
    },
    {
      label: 'Aguardando Aprovação',
      value: dashboard?.pendingCommunications || 0,
      icon: Clock,
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/10',
    },
    {
      label: 'Enviadas',
      value: dashboard?.sentCommunications || 0,
      icon: Send,
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
    },
    {
      label: 'Rejeitadas',
      value: dashboard?.rejectedCommunications || 0,
      icon: XCircle,
      color: 'text-red-400',
      bgColor: 'bg-red-500/10',
    },
  ];

  const urgentDeadlines = deadlines?.filter(d => d.daysRemaining <= 5) || [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Comunicações COAF</h1>
          <p className="text-surface-400">Gestão de comunicações ao Conselho de Controle de Atividades Financeiras</p>
        </div>
        <Link
          to="/admin/compliance/coaf/new"
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg font-medium transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nova Comunicação
        </Link>
      </div>

      {/* Urgent Deadlines Alert */}
      {urgentDeadlines.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-red-400">Prazos Urgentes</h3>
              <p className="text-sm text-surface-300 mt-1">
                {urgentDeadlines.length} comunicação(ões) com prazo próximo do vencimento
              </p>
              <div className="mt-3 space-y-2">
                {urgentDeadlines.slice(0, 3).map((deadline) => (
                  <Link
                    key={deadline.communicationId}
                    to={`/admin/compliance/coaf/${deadline.communicationId}`}
                    className="flex items-center justify-between p-2 bg-surface-800 rounded-lg hover:bg-surface-700 transition-colors"
                  >
                    <span className="text-sm">
                      Comunicação #{deadline.communicationId.slice(0, 8)}
                    </span>
                    <span className={`text-sm font-medium ${deadline.daysRemaining <= 2 ? 'text-red-400' : 'text-yellow-400'}`}>
                      {deadline.daysRemaining} dia(s) restante(s)
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-surface-800 rounded-xl p-6 border border-surface-700">
            <div className="flex items-center justify-between">
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
              <span className="text-3xl font-bold">{stat.value}</span>
            </div>
            <p className="text-surface-400 mt-3">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Status */}
        <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
          <h2 className="text-lg font-semibold mb-4">Por Status</h2>
          <div className="space-y-3">
            {Object.entries(dashboard?.communicationsByStatus || {}).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(status)}`}>
                  {getStatusLabel(status)}
                </span>
                <span className="font-medium">{count as number}</span>
              </div>
            ))}
            {Object.keys(dashboard?.communicationsByStatus || {}).length === 0 && (
              <p className="text-surface-400 text-center py-4">Nenhuma comunicação registrada</p>
            )}
          </div>
        </div>

        {/* By Type */}
        <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
          <h2 className="text-lg font-semibold mb-4">Por Tipo</h2>
          <div className="space-y-3">
            {Object.entries(dashboard?.communicationsByType || {}).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <span className="text-surface-300">{getTypeLabel(type)}</span>
                <span className="font-medium">{count as number}</span>
              </div>
            ))}
            {Object.keys(dashboard?.communicationsByType || {}).length === 0 && (
              <p className="text-surface-400 text-center py-4">Nenhuma comunicação registrada</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Communications */}
      <div className="bg-surface-800 rounded-xl p-6 border border-surface-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Comunicações Recentes</h2>
          <Link
            to="/admin/compliance/coaf/list"
            className="text-primary-400 hover:text-primary-300 text-sm flex items-center gap-1"
          >
            Ver todas <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-surface-400 text-sm border-b border-surface-700">
                <th className="pb-3 font-medium">ID</th>
                <th className="pb-3 font-medium">Tipo</th>
                <th className="pb-3 font-medium">Operação</th>
                <th className="pb-3 font-medium">Valor</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium">Data</th>
                <th className="pb-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-700">
              {dashboard?.recentCommunications?.map((comm) => (
                <tr key={comm.id} className="hover:bg-surface-700/50">
                  <td className="py-3 font-mono text-sm">{comm.id.slice(0, 8)}</td>
                  <td className="py-3">{getTypeLabel(comm.type)}</td>
                  <td className="py-3">{comm.operationType}</td>
                  <td className="py-3">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(comm.operationValue)}
                  </td>
                  <td className="py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(comm.status)}`}>
                      {getStatusLabel(comm.status)}
                    </span>
                  </td>
                  <td className="py-3 text-surface-400 text-sm">
                    {new Date(comm.createdAt).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="py-3">
                    <Link
                      to={`/admin/compliance/coaf/${comm.id}`}
                      className="text-primary-400 hover:text-primary-300 text-sm"
                    >
                      Ver
                    </Link>
                  </td>
                </tr>
              ))}
              {(!dashboard?.recentCommunications || dashboard.recentCommunications.length === 0) && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-surface-400">
                    Nenhuma comunicação encontrada
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/admin/compliance/coaf/list"
          className="bg-surface-800 rounded-xl p-6 border border-surface-700 hover:border-primary-500/50 transition-colors group"
        >
          <FileText className="w-8 h-8 text-primary-400 mb-3" />
          <h3 className="font-semibold group-hover:text-primary-400 transition-colors">Lista de Comunicações</h3>
          <p className="text-sm text-surface-400 mt-1">Ver todas as comunicações COAF</p>
        </Link>
        
        <Link
          to="/admin/compliance/coaf/new"
          className="bg-surface-800 rounded-xl p-6 border border-surface-700 hover:border-primary-500/50 transition-colors group"
        >
          <Plus className="w-8 h-8 text-green-400 mb-3" />
          <h3 className="font-semibold group-hover:text-primary-400 transition-colors">Nova Comunicação</h3>
          <p className="text-sm text-surface-400 mt-1">Criar comunicação manual</p>
        </Link>
        
        <Link
          to="/admin/compliance/pld"
          className="bg-surface-800 rounded-xl p-6 border border-surface-700 hover:border-primary-500/50 transition-colors group"
        >
          <AlertTriangle className="w-8 h-8 text-yellow-400 mb-3" />
          <h3 className="font-semibold group-hover:text-primary-400 transition-colors">Alertas PLD</h3>
          <p className="text-sm text-surface-400 mt-1">Ver alertas que podem gerar comunicações</p>
        </Link>
      </div>
    </div>
  );
}
